"use client"

import { useSyncExternalStore } from "react"
import { Coins, Pencil, Users } from "lucide-react"

import { bonusScheduleLabel } from "@/components/partners/client-bonus"
import { formatPercent } from "@/lib/partners/percent"
import { NameWithBadges } from "@/components/partners/name-with-badges"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CopyButton } from "@/components/ui/copy-button"
import { formatMicroUsd } from "@/lib/billing/utils/format"
import type { PartnerCodeView } from "@/server-actions/partners"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




/** One referral link: the link to share and the code to type at signup, its split and its results. */
export function ReferralLinkRow({
  code,
  onEdit,
  onToggleActive,
  togglePending,
}: {
  code: PartnerCodeView
  onEdit: () => void
  onToggleActive: () => void
  togglePending: boolean
}) {

  const tBonus = useTranslations("PartnerBonus")
  const tUi = useTranslations("Partners.PartnersReferralLinkRow")

  const origin = useSyncExternalStore(subscribeToNothing, () => window.location.origin, () => "")
  const link = `${origin}/auth/register?ref=${encodeURIComponent(code.code)}`
  const partnerPercent = Math.max(0, code.commission_percent - code.client_bonus_percent)
  const displayName = code.label ?? tUi("referralLink")

  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <NameWithBadges name={displayName}>
          {!code.is_active && <Badge variant="secondary"><span key={tUi("inactive")}>{tUi("inactive")}</span></Badge>}
        </NameWithBadges>
        <div className="flex shrink-0 gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit}><TranslationText>{tUi.rich("tag0Tag0Edit", { tag0: () => (<Pencil className="mr-1.5 h-4 w-4" />) })}</TranslationText></Button>
          <Button variant="ghost" size="sm" disabled={togglePending} onClick={onToggleActive}>
            <span key={code.is_active ? tUi("deactivate") : tUi("activate")}>{code.is_active ? tUi("deactivate") : tUi("activate")}</span>
          </Button>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <ShareValue label={tUi("link")} value={link} shown={link.replace(/^https?:\/\//, "")} />
        <ShareValue label={tUi("code")} value={code.code} shown={code.code} />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
          {<span key={tUi("youValue0", { value0: String(formatPercent(partnerPercent)) })}>{tUi("youValue0", { value0: String(formatPercent(partnerPercent)) })}</span>}
        </span>
        {code.client_bonus_percent > 0 && (
          <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700">
            {<span key={tUi("clientValue0Value1", { value0: String(formatPercent(code.client_bonus_percent)), value1: String(bonusScheduleLabel(code.client_bonus_payment_limit, tBonus)) })}>{tUi("clientValue0Value1", { value0: String(formatPercent(code.client_bonus_percent)), value1: String(bonusScheduleLabel(code.client_bonus_payment_limit, tBonus)) })}</span>}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <Users className="h-3.5 w-3.5" aria-hidden />
          <span>{<span key={tUi("value0Referred", { value0: String(code.referred_count) })}>{tUi("value0Referred", { value0: String(code.referred_count) })}</span>}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <Coins className="h-3.5 w-3.5" aria-hidden />
          <span>{<span key={tUi("value0Earned", { value0: String(formatMicroUsd(code.earned_microusd)) })}>{tUi("value0Earned", { value0: String(formatMicroUsd(code.earned_microusd)) })}</span>}</span>
        </span>
      </div>
    </div>
  )
}

function ShareValue({ label, value, shown }: { label: string; value: string; shown: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-md bg-muted/60 py-1 pl-3 pr-1">
      <span className="shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 truncate font-mono text-xs" title={value}>{shown}</span>
      <CopyButton value={value} label={label} />
    </div>
  )
}

function subscribeToNothing(): () => void {
  return () => {}
}
